import { useState, FormEvent } from 'react';
import { useInView } from '../../hooks/useInView';

interface FinalCTAProps {
  eyebrow?: string;
  headline: string;
  subheadline: string;
  ctaText?: string;
}

const experienceLevels = [
  'Complete Beginner',
  'Tried Snorkeling',
  'Open Water Certified',
  'Advanced Diver',
  'Rescue Diver / Divemaster',
  'Technical Diver',
];

export default function SectionFinalCTA({
  eyebrow = 'Get Started',
  headline,
  subheadline,
  ctaText = 'Get Started Today',
}: FinalCTAProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { ref, inView } = useInView();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => { data.append(key, value as string); });

    try {
      const res = await fetch('https://readdy.ai/api/form/d73u7i341deorl02eijg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) setSubmitted(true);
      else setError('Something went wrong. Please try again.');
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <section id="contact" className="relative py-28 md:py-40 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/3d38cd7f2af1b7c4107cbee4e1a80213.jpg"
          alt="Contact Ace Diving Miami — Start Your Journey"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020C1B]/97 via-[#060F1D]/88 to-[#0A1628]/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020C1B]/60 via-transparent to-[#020C1B]/40" />
      </div>
      {/* Side accent */}
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#00B4D8]/30 to-transparent hidden xl:block" />

      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        className={`relative z-10 max-w-7xl mx-auto px-6 transition-all duration-700 ${
          inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="flex flex-col lg:flex-row gap-16 xl:gap-24 items-start">
          {/* Left — brand proof */}
          <div className="lg:w-[48%]">
            <div className="flex items-center gap-4 mb-6">
              <div className="h-px w-14 bg-gradient-to-r from-transparent to-[#00B4D8]" />
              <span className="text-[#00B4D8] text-[10px] font-black uppercase tracking-[0.35em]">{eyebrow}</span>
            </div>
            <h2
              className="text-4xl md:text-[52px] font-black text-white mb-6 leading-[1.02]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {headline}
            </h2>
            <p className="text-white/50 text-[17px] leading-relaxed mb-10 max-w-md">{subheadline}</p>

            {/* Phone + WhatsApp */}
            <div className="flex flex-col sm:flex-row gap-3 mb-12">
              <a
                href="tel:+17862413194"
                className="inline-flex items-center justify-center gap-2.5 bg-white text-[#0D1B2A] hover:bg-[#00B4D8] hover:text-white font-bold px-7 rounded-full transition-all duration-300 text-[15px] whitespace-nowrap cursor-pointer"
                style={{ paddingTop: '18px', paddingBottom: '18px' }}
              >
                <i className="ri-phone-fill" />
                +1 786-241-3194
              </a>
              <a
                href="https://wa.me/17862413194"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center justify-center gap-2.5 bg-[#25D366]/15 border border-[#25D366]/35 hover:bg-[#25D366] text-white font-bold px-7 rounded-full transition-all duration-300 text-[15px] whitespace-nowrap cursor-pointer"
                style={{ paddingTop: '18px', paddingBottom: '18px' }}
              >
                <i className="ri-whatsapp-line text-[#25D366]" />
                WhatsApp Us
              </a>
            </div>

            {/* Trust signals */}
            <div className="space-y-4">
              {[
                { icon: 'ri-time-line', label: 'Response within a few hours' },
                { icon: 'ri-shield-check-line', label: 'No-obligation conversation' },
                { icon: 'ri-map-pin-line', label: 'Pier 5, 401 Biscayne Blvd, Miami FL 33132' },
                { icon: 'ri-mail-line', label: 'info@ace-diving.com' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-[#00B4D8] text-[15px]`} />
                  </div>
                  <span className="text-white/40 text-[14px]">{item.label}</span>
                </div>
              ))}
            </div>

            {/* Agency badges row */}
            <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-white/8">
              {['PADI', 'SSI', 'SDI', 'TDI', 'ERDI'].map((ag) => (
                <span
                  key={ag}
                  className="bg-white/6 border border-white/12 text-white/55 text-[10px] font-black px-3 py-1.5 rounded-full tracking-widest"
                >
                  {ag}
                </span>
              ))}
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:w-[52%] w-full">
            <div className="bg-white rounded-2xl p-8 md:p-10 relative overflow-hidden">
              {/* Top accent */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#00B4D8] via-[#00C9F0] to-[#00B4D8]" />

              {!submitted ? (
                <>
                  <h3
                    className="text-[26px] font-black text-[#0D1B2A] mb-1.5"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Start Your Diving Journey
                  </h3>
                  <p className="text-[#687585] text-sm mb-8 leading-relaxed">
                    A dive specialist will contact you within a few hours. <strong className="text-[#0D1B2A]">No commitment required.</strong>
                  </p>

                  <form
                    data-readdy-form
                    id="ace-diving-lead-form-main"
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] font-black text-[#374151] mb-1.5 uppercase tracking-wider">
                          Full Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="Your full name"
                          className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3.5 text-sm text-[#0D1B2A] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/15 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-black text-[#374151] mb-1.5 uppercase tracking-wider">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          placeholder="Your phone number"
                          className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3.5 text-sm text-[#0D1B2A] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/15 transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-black text-[#374151] mb-1.5 uppercase tracking-wider">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="your@email.com"
                        className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3.5 text-sm text-[#0D1B2A] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/15 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-black text-[#374151] mb-1.5 uppercase tracking-wider">
                        Experience Level
                      </label>
                      <select
                        name="experience_level"
                        required
                        className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3.5 text-sm text-[#0D1B2A] focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/15 transition-all bg-white cursor-pointer"
                      >
                        <option value="">Select your level</option>
                        {experienceLevels.map((level) => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-[11px] font-black text-[#374151] mb-1.5 uppercase tracking-wider">
                        Message (Optional)
                      </label>
                      <textarea
                        name="message"
                        rows={3}
                        placeholder="Tell us about your goals, schedule, or questions..."
                        maxLength={500}
                        className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3.5 text-sm text-[#0D1B2A] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/15 transition-all resize-none"
                      />
                    </div>

                    {error && <p className="text-red-500 text-xs">{error}</p>}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#00B4D8] hover:bg-[#009FBF] disabled:opacity-60 text-white font-black py-4.5 rounded-xl transition-all duration-300 text-[15px] whitespace-nowrap cursor-pointer"
                      style={{ paddingTop: '18px', paddingBottom: '18px' }}
                    >
                      {loading ? 'Sending…' : ctaText}
                    </button>

                    <p className="text-[#9CA3AF] text-xs text-center">
                      No spam. Your information is only used to respond to your inquiry.
                    </p>
                  </form>
                </>
              ) : (
                <div className="text-center py-12">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-[#E8F8FC] mx-auto mb-6">
                    <i className="ri-check-line text-[#00B4D8] text-4xl" />
                  </div>
                  <h3
                    className="text-2xl font-black text-[#0D1B2A] mb-3"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    You&apos;re All Set!
                  </h3>
                  <p className="text-[#687585] text-[15px] leading-relaxed max-w-sm mx-auto">
                    Thank you for reaching out. A dive specialist will contact you shortly to discuss your goals and recommend the ideal program.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
